/**
 * Shared round-type constants used across multiple pages/components.
 * Extracted here so that NewRound.jsx can be code-split (lazy-loaded)
 * without dragging its constants into the main bundle via static imports
 * from FilterPanel.jsx and Stats.jsx.
 */

import faceDefinitions from "../../../shared/face-definitions.json";

const FACE_ENTRIES = Object.entries(faceDefinitions.faces);

// ── Face label map ────────────────────────────────────────────────────────────
// Maps target-face codes to human-readable display names, sourced from
// shared/face-definitions.json (issue #1680 PR 2) — the same JSON the backend
// loader (backend/app/face_definitions.py / scorecard/base.py FACE_LABEL)
// reads, so the two can't drift.
//
// Labels carry the explicit physical face size in cm (#1440) so the wizard,
// the per-archer FacePanel/FaceDropdown chips, the FORMAT list and the
// scorecard all surface the size. 3D / animal faces have no ring radius
// (face_size_cm = null in backend/app/faces.py) so they stay size-free.
export const FACE_LABEL = Object.fromEntries(
  FACE_ENTRIES.map(([code, entry]) => [code, entry.display_label]),
);

// ── Face-type (ring-layout) label map ───────────────────────────────────────────
// Maps ring-layout `face_type` ids (from backend/app/faces.py FaceDef ids, the
// namespace that `by_distance.face_type` uses) to friendly archery names. These
// ids are NOT the target_face codes that FACE_LABEL above maps — keep them apart.
//
// Derived from the `display_label` on each `faceTypes` row of
// shared/face-definitions.json (#2038). It used to be hand-written and covered
// only 8 of the 17 layouts, so the Stats distance-breakdown header fell through
// to a title-cased id ("Wa Field") or the raw `wa_field_3spot`. Adding a layout
// to the JSON now labels it everywhere; the bidirectional parity test in
// faceTableParity.test.js fails if a row ever lands without a display_label.
export const FACE_TYPE_LABEL = Object.fromEntries(
  Object.entries(faceDefinitions.faceTypes).map(([id, entry]) => [
    id,
    entry.display_label,
  ]),
);

// ── Face group map ─────────────────────────────────────────────────────────────
// Maps each target-face code to its face-group, sourced from
// shared/face-definitions.json (issue #1680 PR 2) — mirrors the backend loader
// (backend/app/face_definitions.py). Faces in the same group are compatible
// for bale-session mixing.
//
// Notable groupings: a WA 6-ring face mis-scores beside a full 122 cm face on
// a shared bale, so it gets its own "wa-6ring" group rather than joining
// "wa-outdoor". WA 5-ring faces (#1447) DO join "wa-outdoor" (their miss
// boundary aligns with ring 6 of the full face, safe to mix on a shared bale).
export const FACE_GROUP = Object.fromEntries(
  FACE_ENTRIES.map(([code, entry]) => [code, entry.group]),
);

/**
 * Returns all face codes that belong to a given face group.
 * @param {string} group - a face_group string (e.g. "wa-spot")
 * @returns {string[]} array of face codes in that group
 */
export function facesForGroup(group) {
  return Object.entries(FACE_GROUP)
    .filter(([, g]) => g === group)
    .map(([face]) => face);
}

// ── WA ten-ring format types — RETIRED (#2162) ───────────────────────────────
// `WA_TEN_RING_TYPES` held the two DISCIPLINE tile keys ('indoor_wa',
// 'outdoor_wa') whose rounds score on the WA ten-ring family, and gated the
// per-archer inner-ten toggle (#1401) plus FORMAT's passive scoring note.
// #2097 retired the discipline dimension: the reducer now writes `formatType`
// from exactly three places — SELECT_FORMAT_TYPE ('practice' / 'catalog' /
// 'custom', the only three TYPE tiles), PICK_RECENT (null) and
// SELECT_ROUND_TYPE (null) — so neither key can reach the set and
// `WA_TEN_RING_TYPES.has(formatType)` was permanently false at both call sites.
// The surviving rule is the SELECTED ROUND's own `scoring_family`
// (`isWaTenRingFormat` in lib/formatMatching) and, on ARCHERS, "a catalog round
// is in play" — see `showInnerTen` in WizardStepArchers.

// ── Format-type filter map — RETIRED (#2097) ─────────────────────────────────
// `FORMAT_TYPE_FILTERS`, `matchesFormatFilter` and `TILE_FACES` used to map each
// WizardStepType DISCIPLINE tile (indoor_wa / outdoor_wa / field / 3d / imperial
// / …) to a catalog filter. #1426 replaced the discipline tile grid with the
// Simple Round / Pick a format / Custom Format tiles, and since then the only
// keys reaching the wizard are 'practice', 'catalog' and 'custom' — none of
// which has a filter entry. The map was live code with no user path to reach it,
// so wave 1 found two tiles rotted to match ZERO seeded rounds (#2027) with
// nobody noticing. Owner decision 2026-08-04: retire it.
//
// The FORMAT step now shows the whole non-legacy catalog and narrows it with the
// search box (`nonLegacyRoundTypes` + `matchesCatalogSearch` in lib/formatMatching).

// ── Open-ended round cap ─────────────────────────────────────────────────────
// Hard cap on ends for an open-ended round (spec 2026-09-13). Mirrors
// `backend/app/services/round_lifecycle.OPEN_ENDED_MAX_ENDS` — the server is
// the source of truth (409 `all_ends_shot` on the 61st confirm/skip); this
// value only needs to match so the client can stop offering a new row/confirm
// before that round-trip.
export const OPEN_ENDED_MAX_ENDS = 60;